import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import { Autocomplete, Container, Row, Col, Text, Icon } from '@pinpoint/design-kit';

import Message from 'src/components/message';
import Button from 'src/components/button';
import { clientsEditActions } from 'src/store/actions';
import { Page } from 'src/containers';
import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';

// NOTE: Experimenting with redux Hooks instead of HOC

function EditClientSubs() {
  const [acOpen, setAcOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const { clientId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const clientName = useSelector((state) => state?.clientsEdit?.client?.name);
  const tenant = useSelector((state) => state?.clientsEdit?.client?.tenant);
  const subscribed = useSelector((state) => state?.clientsEdit?.subscribed);
  const possibleSubscribers = useSelector((state) => state?.clientsEdit?.possibleSubscribers);
  const updateSubscribedStatus = useSelector((state) => state?.clientsEdit?.updateSubscribedStatus);
  const fetchSubscribedStatus = useSelector((state) => state?.clientsEdit?.fetchSubscribedStatus);
  const lastCmd = useSelector((state) => state?.clientsEdit?.lastCmd);
  const ready = useSelector(
    (state) =>
      state?.clientsEdit?.fetchSubscribedStatus === CONSTANTS.API.SUCCEEDED &&
      state?.clientsEdit?.fetchPossibleSubscribersStatus === CONSTANTS.API.SUCCEEDED
  );

  const fetchSubscribed = useCallback(
    () =>
      dispatch(
        clientsEditActions.fetchSubscribed({
          filters: [
            {
              name: 'object_id',
              op: 'eq',
              val: tenant,
            },
          ],
          order_by: [
            {
              field: 'created_at',
              direction: 'desc',
            },
          ],
        })
      ),
    [dispatch, tenant]
  );

  const toggleAcDropdown = useCallback(() => setAcOpen(!acOpen), [acOpen]);
  const handleSelection = useCallback(
    (idx) => {
      setSelectedUser({ ...(possibleSubscribers?.[idx] ?? { email: '' }) });
    },
    [possibleSubscribers]
  );
  const addSubscribed = useCallback(
    (evt) => {
      evt.preventDefault();
      const { subscriberType } = evt.currentTarget.dataset;
      const subscriberId = +evt.currentTarget.dataset.subscriberId;
      const params = { subscriberId, subscriberType, objectType: 'client', objectId: tenant };
      dispatch(clientsEditActions.updateSubscribed('add', params));
    },
    [dispatch, tenant]
  );
  const delSubscribed = useCallback(
    (evt) => {
      const id = evt.currentTarget.dataset.val;
      dispatch(clientsEditActions.updateSubscribed('del', { id }));
    },
    [dispatch]
  );

  useEffect(() => {
    if (updateSubscribedStatus === CONSTANTS.API.SUCCEEDED) {
      fetchSubscribed();
      setSelectedUser({});
    }
  }, [updateSubscribedStatus, lastCmd, fetchSubscribed, dispatch]);

  useEffect(() => {
    if (fetchSubscribedStatus === CONSTANTS.API.SUCCEEDED) dispatch(clientsEditActions.fetchPossibleSubscribers());
  }, [dispatch, subscribed, fetchSubscribedStatus]);

  useEffect(() => {
    if (!tenant) dispatch(clientsEditActions.fetchClient(clientId));
    else fetchSubscribed();
  }, [clientId, fetchSubscribed, dispatch, tenant]);

  useEffect(
    () => () => {
      dispatch(clientsEditActions.unset('subscribed'));
      dispatch(clientsEditActions.unset('fetchSubscribedStatus'));
      dispatch(clientsEditActions.unset('fetchSubscribedError'));
      dispatch(clientsEditActions.unset('possibleSubscribers'));
      dispatch(clientsEditActions.unset('fetchPossibleSubscribersStatus'));
      dispatch(clientsEditActions.unset('fetchPossibleSubscribersError'));
      dispatch(clientsEditActions.unset('lastCmd'));
      dispatch(clientsEditActions.unset('updateSubscribedStatus'));
      dispatch(clientsEditActions.unset('updateSubscribedError'));
    },
    [dispatch]
  );

  if (!ready) return <Message />;
  return (
    <Container
      w="100%"
      minW="100%"
      h="100%"
      minH="100%"
      data-debug="container"
      d="flex"
      flexDir="column"
      overflow="hidden"
    >
      <Row data-debug="nav-row" p={{ t: '1.5rem' }} m={{ x: '.5rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="12" align="center">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push(ROUTES.CLIENTS_EDIT.replace(':clientId', clientId));
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
        </Col>
      </Row>
      <Row data-debug="title-row" p={{ t: '1.5rem' }} m={{ x: '.5rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="12" align="center">
          <Text textSize="title">{clientName} Subscribers</Text>
        </Col>
      </Row>
      {subscribed.map((elem) => (
        <Row p={{ t: '1.5rem' }} key={elem.id} style={{ gap: '.5rem' }}>
          <Col size="6" textColor="black">
            <Text textSize="paragraph">{elem.email}</Text>
          </Col>
          <Col textColor="black">
            <Button
              data-val={elem.id}
              buttonStyle="primary"
              w="1.875rem"
              onClick={delSubscribed}
              data-tip={`Unsubscribe "${elem.email}"`}
            >
              <Icon color="white" name="Delete" viewBox="0 0 50 50" size=".875rem" cursor="pointer" />
            </Button>
          </Col>
        </Row>
      ))}
      <Row p={{ t: '1.5rem' }} style={{ gap: '.5rem' }}>
        <Col textColor="black" size="6">
          <Autocomplete
            menu={possibleSubscribers.map(({ email }) => email)}
            onClick={toggleAcDropdown}
            value={selectedUser?.email ?? ''}
            isOpen={acOpen}
            onSelection={handleSelection}
            openSuffix={<Icon color="shade66" name="UpArrow" size=".625rem" />}
            closeSuffix={<Icon color="shade66" name="DownArrow" size=".625rem" />}
            DropdownButtonOptions={{
              h: '1.875rem',
              w: '2rem',
              rounded: { tr: 'md', br: 'md' },
              border: { y: '1px solid', r: '1px solid', l: '0' },
              borderColor: 'gray500',
              hoverBg: 'white',
              bg: 'white',
              align: 'center',
              justify: 'center',
              d: 'inline-block',
              p: '0',
              transition: 'all 0.2s ease-in-out',
            }}
            inputStyle={{
              h: 'calc(1.875rem-1px)',
              textWeight: '500',
              bg: 'white',
              hoverBg: 'white',
              style: {
                transition: 'all 0.2s ease-in-out',
                font: 'inherit',
              },
            }}
            DivOptions={{
              bg: 'white',
              rounded: acOpen ? { tl: 'md', bl: '0', br: '0', tr: '0' } : { l: 'md', r: '0' },
              border: acOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid',
              style: {
                transition: 'all 0.2s ease-in-out',
              },
            }}
            menuItemOptions={{
              p: { y: '.25rem', x: '.75rem' },
              textWeight: '500',
              bg: 'white',
              hoverBg: 'shadeF2',
            }}
            menuOptions={{
              border: { b: '1px solid', x: '1px solid', t: '0' },
              borderColor: 'gray500',
              rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
              pos: 'absolute',
              shadow: 4,
              minW: 'calc(100% - 2rem)',
              maxW: 'calc(100% - 2rem)',
              style: {
                transition: 'all 0.2s ease-in-out',
              },
              maxH: '250px',
              overflow: 'auto',
              m: { t: '-2px' },
            }}
          />
        </Col>
        <Col>
          <Button
            buttonStyle="primary"
            onClick={addSubscribed}
            data-tip="Subscribe user"
            data-subscriber-id={selectedUser?.id}
            data-subscriber-type="user"
            data-object-id={tenant}
            data-object-type="client"
          >
            <Icon color="white" name="AddBoxed" viewBox="0 0 14 14" size=".875rem" cursor="pointer" />
          </Button>
        </Col>
      </Row>
    </Container>
  );
}

EditClientSubs.propTypes = {};

export default Page(EditClientSubs);
